import type { UseQueryResult } from '@tanstack/react-query';
import { first } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { type JobItemDetails, useJobItemDetails } from '../api/use-job-item-details';
import { useJobItemVersions } from '../api/use-job-item-versions';
import { useJobArchitectureUrlTileActions } from '../url-tiles/use-job-architecture-url-tile-actions';
import { useDraftJobItemDetails } from './use-draft-job-item-details';

export const useGetActiveJobProfileVersion = (
    itemId: string,
    itemType: 'job-level' | 'job-role',
    architectureId: string,
    initialVersion?: string,
): {
    readonly activeVersion: string | undefined;
    readonly error: Error | null;
    readonly isLoading: boolean;
    readonly item: JobItemDetails | undefined;
    readonly setActiveVersion: (version: string) => void;
} => {
    const { updateJobProfileTileVersion } = useJobArchitectureUrlTileActions();
    const [activeVersion, setActiveVersionInternal] = useState<string | undefined>(initialVersion);
    const [resolvedItemId, setResolvedItemId] = useState<string | null>(null);

    useEffect(() => {
        if (initialVersion !== undefined && initialVersion !== activeVersion) {
            setActiveVersionInternal(initialVersion);
        }
    }, [initialVersion, activeVersion]);
    const { data: versions, isLoading: isLoadingVersions } = useJobItemVersions({
        architectureId,
        itemId,
        itemType,
    });

    const itemIdChanged = resolvedItemId !== itemId;

    const { draftData, hasDraft } = useDraftJobItemDetails({
        itemId,
    });

    const queryResult: UseQueryResult<JobItemDetails, Error> = useJobItemDetails({
        architectureId,
        enabled: !itemIdChanged && !isLoadingVersions && !hasDraft,
        itemId,
        itemType,
        version: activeVersion,
    });

    const item = useMemo((): JobItemDetails | undefined => {
        if (hasDraft && draftData !== undefined) {
            return draftData;
        }

        return queryResult.data;
    }, [hasDraft, draftData, queryResult.data]);

    const error = useMemo((): Error | null => {
        if (hasDraft) {
            return null;
        }

        return queryResult.error ?? null;
    }, [hasDraft, queryResult.error]);

    const isLoading = useMemo((): boolean => {
        if (itemIdChanged) {
            return true;
        }

        if (hasDraft) {
            return isLoadingVersions;
        }

        return queryResult.isLoading || isLoadingVersions;
    }, [hasDraft, itemIdChanged, queryResult.isLoading, isLoadingVersions]);

    useEffect(() => {
        if (!isLoadingVersions && itemIdChanged) {
            setActiveVersionInternal(initialVersion ?? first(versions)?.version.toString());
            setResolvedItemId(itemId);
        }
    }, [itemId, isLoadingVersions, itemIdChanged, versions, initialVersion]);

    const setActiveVersion = useCallback(
        (version: string) => {
            setActiveVersionInternal(version);
            const numericVersion = Number(version);
            if (!Number.isNaN(numericVersion)) {
                updateJobProfileTileVersion(numericVersion);
            }
        },
        [updateJobProfileTileVersion],
    );

    return {
        activeVersion,
        error,
        isLoading,
        item,
        setActiveVersion,
    };
};
